import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Pesquisa, PesquisaItemPergunta, PesquisaItemResposta } from '@gestor-web/@core/api/ords/model';
import { OperadorasSelectors, UserProfileSelectors } from '@gestor-web/@core/store/selectors';
import { Store } from '@ngrx/store';
import { NotificationService } from '@gestor-web/app/shared/services';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { PesquisaNpsDialogService } from './pesquisa-nps-dialog.service';

type TipoPergunta = 'alternativas' | 'escala' | 'texto';

interface PerguntaView {
  pergunta: PesquisaItemPergunta;
  tipo: TipoPergunta;
  controlName: string;
}

const MS_POR_DIA = 1000 * 60 * 60 * 24;
const MS_POR_HORA = 1000 * 60 * 60;

// Limite de caracteres do comentário livre. Espelha o tamanho da coluna vl_resposta
// (VARCHAR2(500)) no backend: acima disso o envio falha, então travamos no campo.
const MAX_JUSTIFICATIVA_CHARS = 500;

@Component({
    selector: 'bl-pesquisa-nps-dialog',
    templateUrl: './pesquisa-nps-dialog.component.html',
    styleUrls: ['./pesquisa-nps-dialog.component.scss'],
    standalone: false
})
export class PesquisaNpsDialogComponent implements OnInit, OnDestroy {
  @Input() cdPesquisa: number;

  @Output() onClose = new EventEmitter<void>();
  @Output() onRespondida = new EventEmitter<number>();

  visible = false;

  pesquisa: Pesquisa | null = null;
  perguntasView: PerguntaView[] = [];
  form: UntypedFormGroup;

  carregando = false;
  enviando = false;

  // Régua NPS 0-10.
  readonly escalaOptions = Array.from({ length: 11 }, (_, i) => ({ label: `${i}`, value: i }));

  readonly maxJustificativaChars = MAX_JUSTIFICATIVA_CHARS;

  private regans: string;
  private destroyed$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private service: PesquisaNpsDialogService,
    private notification: NotificationService,
  ) {}

  ngOnInit() {
    // Reage à troca de operadora (regans) em tempo real: a cada operadora distinta o estado
    // é reiniciado e a pesquisa é rebuscada, reavaliando a elegibilidade, sem precisar de reload.
    // A equipe interna Blendus (`selectIsAdmin`) é excluída para não poluir o
    // NPS real; o gate fica no filter, antes do fetch, então nem chega a chamar o backend.
    //  - distinctUntilChanged: ignora emissões repetidas do mesmo par (operadora, isAdmin)
    //  - tap: efeito colateral síncrono (guarda o regans atual e limpa o card anterior)
    //  - filter: só segue adiante quando há operadora e cd_pesquisa e o usuário não é admin
    //  - switchMap: troca a busca anterior pela nova, cancelando a antiga se ainda estiver em voo
    combineLatest([
      this.store.select(OperadorasSelectors.selectWorkspaceOperadoraId),
      this.store.select(UserProfileSelectors.selectIsAdmin),
    ])
      .pipe(
        distinctUntilChanged(([regansA, adminA], [regansB, adminB]) => regansA === regansB && adminA === adminB),
        tap(([regans]) => {
          this.regans = regans;
          this.resetEstado();
        }),
        filter(([regans, isAdmin]) => !!regans && !!this.cdPesquisa && !isAdmin),
        switchMap(([regans]) => {
          this.carregando = true;
          return this.service
            .fetchPesquisa(this.cdPesquisa, regans)
            .pipe(finalize(() => (this.carregando = false)));
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe(pesquisa => {
        this.pesquisa = pesquisa;
        if (pesquisa && this.deveExibir(pesquisa)) {
          this.montarPerguntasView(pesquisa);
          this.montarForm();
          this.visible = true;
        }
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private resetEstado() {
    this.visible = false;
    this.pesquisa = null;
    this.perguntasView = [];
  }

  private deveExibir(p: Pesquisa): boolean {
    if (p.ib_ativa !== 1) {
      return false;
    }
    if (!p.dt_ultima_resposta) {
      return true;
    }
    const decorridoMs = Date.now() - new Date(p.dt_ultima_resposta).getTime();
    if (p.tp_ultima_acao === 'R') {
      return decorridoMs / MS_POR_DIA > p.nr_intervalo_dias;
    }
    if (p.tp_ultima_acao === 'A') {
      return decorridoMs / MS_POR_HORA > p.nr_intervalo_recusa_hrs;
    }
    // Há data mas tipo de ação desconhecido: não reexibe (estado inesperado).
    return false;
  }

  // Convenção de renderização (ver TipoPergunta): perguntas com alternativas viram
  // múltipla escolha; entre as sem alternativas, a primeira (por nr_ordem) é a escala
  // NPS 0-10 e as demais são comentário livre. Ajustar aqui se o cadastro mudar.
  private montarPerguntasView(pesquisa: Pesquisa) {
    const perguntas = [...(pesquisa.perguntas ?? [])].sort((a, b) => a.nr_ordem - b.nr_ordem);
    let escalaDefinida = false;

    this.perguntasView = perguntas.map(pergunta => {
      let tipo: TipoPergunta;
      if (pergunta.alternativas?.length) {
        tipo = 'alternativas';
      } else if (!escalaDefinida) {
        tipo = 'escala';
        escalaDefinida = true;
      } else {
        tipo = 'texto';
      }
      return { pergunta, tipo, controlName: `p_${pergunta.cd_item_pergunta}` };
    });
  }

  private montarForm() {
    const group: Record<string, unknown> = {};
    this.perguntasView.forEach(({ tipo, controlName }) => {
      // Escala e múltipla escolha são obrigatórias; comentário é opcional, mas
      // limitado ao tamanho suportado pela coluna vl_resposta (evita erro no envio).
      const validators =
        tipo === 'texto' ? [Validators.maxLength(MAX_JUSTIFICATIVA_CHARS)] : [Validators.required];
      group[controlName] = [null, validators];
    });
    this.form = this.fb.group(group);
  }

  enviar() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const respostas: PesquisaItemResposta[] = this.perguntasView
      .map(({ pergunta, tipo, controlName }): PesquisaItemResposta | null => {
        const valor = this.form.get(controlName)?.value;
        if (valor == null || valor === '') {
          return null;
        }
        if (tipo === 'alternativas') {
          return { cd_item_pergunta: pergunta.cd_item_pergunta, cd_item_alternativa: valor };
        }
        return { cd_item_pergunta: pergunta.cd_item_pergunta, vl_resposta: `${valor}` };
      })
      .filter((item): item is PesquisaItemResposta => item !== null);

    this.enviando = true;
    this.service
      .saveResposta(this.cdPesquisa, this.regans, { tp_acao: 'R', respostas })
      .pipe(
        finalize(() => (this.enviando = false)),
        takeUntil(this.destroyed$),
      )
      .subscribe(cdResposta => {
        this.notification.success('Resposta enviada com sucesso. Obrigado!');
        this.onRespondida.emit(cdResposta);
        this.fechar();
      });
  }

  // "Agora não": registra o adiamento ('A') para o backend aplicar o intervalo de recusa
  // antes de reexibir.
  adiar() {
    this.fechar();
    this.service
      .saveResposta(this.cdPesquisa, this.regans, { tp_acao: 'A' })
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
  }

  fechar() {
    this.visible = false;
    this.onClose.emit();
  }
}
